import React, { useState } from "react";
import { Link, useSearchParams, useNavigate } from "react-router-dom";
import { api, formatApiError } from "../lib/api";
import { Logo, useTagline } from "../components/Logo";
import { toast } from "sonner";
import { Lock, ArrowRight, CheckCircle2 } from "lucide-react";

export default function ResetPassword() {
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const nav = useNavigate();
  const tagline = useTagline();

  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [err, setErr] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    if (password.length < 6) return setErr("Password must be at least 6 characters.");
    if (password !== confirm) return setErr("Passwords don't match.");
    if (!token) return setErr("Reset token missing from URL.");
    setSubmitting(true);
    try {
      await api.post("/auth/reset-password", { token, password });
      setDone(true);
      toast.success("Password updated. You can now sign in.");
      setTimeout(() => nav("/login"), 1500);
    } catch (e) {
      setErr(formatApiError(e.response?.data?.detail) || e.message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-white">
      <div className="hidden lg:flex flex-col justify-between p-12 bg-blue-600 text-white relative overflow-hidden">
        <div className="absolute inset-0 ym-grid-bg opacity-30" />
        <Logo inverted />
        <div className="relative">
          <h2 className="font-heading font-black text-4xl leading-tight">
            Set a new<br />password.
          </h2>
          <p className="mt-4 text-blue-100 italic">"{tagline}"</p>
        </div>
        <div className="relative text-blue-100 text-sm">© Young Minds Smart Learning Platform</div>
      </div>

      <div className="flex flex-col justify-center p-6 sm:p-12">
        <div className="lg:hidden mb-6"><Logo /></div>
        <div className="max-w-md w-full mx-auto">
          <h1 className="font-heading font-bold text-3xl text-slate-900">Reset password</h1>
          <p className="mt-2 text-slate-600">Choose a strong password you'll remember.</p>

          {!done ? (
            <form onSubmit={submit} className="mt-8 space-y-4" data-testid="reset-form">
              <div>
                <label className="text-sm font-semibold text-slate-700">New password</label>
                <div className="mt-1.5 relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                  <input type="password" required minLength={6} value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    data-testid="reset-password"
                    className="w-full pl-10 pr-4 py-3 rounded-lg border border-slate-200 focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20 outline-none" />
                </div>
              </div>
              <div>
                <label className="text-sm font-semibold text-slate-700">Confirm password</label>
                <div className="mt-1.5 relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                  <input type="password" required minLength={6} value={confirm}
                    onChange={(e) => setConfirm(e.target.value)}
                    data-testid="reset-confirm"
                    className="w-full pl-10 pr-4 py-3 rounded-lg border border-slate-200 focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20 outline-none" />
                </div>
              </div>
              {err && <div data-testid="reset-error" className="text-sm text-red-600 bg-red-50 border border-red-100 rounded-lg p-3">{err}</div>}
              <button type="submit" disabled={submitting} data-testid="reset-submit"
                className="w-full inline-flex items-center justify-center gap-2 px-5 py-3 rounded-lg bg-blue-600 text-white font-semibold hover:bg-blue-700 disabled:opacity-60">
                {submitting ? "Updating..." : "Update password"} <ArrowRight className="h-4 w-4" />
              </button>
            </form>
          ) : (
            <div className="mt-8 rounded-xl bg-emerald-50 border border-emerald-200 p-6 text-emerald-900 text-center" data-testid="reset-success">
              <CheckCircle2 className="h-10 w-10 mx-auto text-emerald-500" />
              <h3 className="mt-3 font-heading font-bold text-lg">Password updated</h3>
              <p className="mt-1 text-sm">Redirecting you to sign in…</p>
            </div>
          )}

          <p className="mt-6 text-sm text-slate-600 text-center">
            Remembered it? <Link to="/login" className="font-semibold text-blue-600 hover:underline">Sign in</Link>
          </p>
        </div>
      </div>
    </div>
  );
}
